import type {
	LiveKitEventRecord,
	LiveKitParticipant,
	LiveKitRoom,
} from "@repo/common";
import {
	type CollectionReference,
	collection,
	type DocumentReference,
	doc,
	type Firestore,
	type FirestoreDataConverter,
	getDoc,
	type QueryDocumentSnapshot,
	type SnapshotOptions,
	Timestamp,
} from "firebase/firestore";

export { type LiveKitRoom, type LiveKitParticipant, type LiveKitEventRecord };

const liveKitEventConverter: FirestoreDataConverter<LiveKitEventRecord> = {
	toFirestore(value) {
		return value.raw ?? {};
	},
	fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions) {
		const data = snapshot.data(options) as Record<string, unknown>;
		const id = ensureString(data.id) ?? snapshot.id;
		const createdAt = parseDate(
			data.createdAt ?? data.created_at ?? data.timestamp,
		);

		return {
			id,
			type: ensureString(data.event ?? data.type) ?? "livekit.event",
			createdAt,
			room: parseRoom(data.room),
			participant: parseParticipant(data.participant),
			egressInfo: pickRecord(data.egressInfo),
			ingressInfo: pickRecord(data.ingressInfo),
			recordingInfo: pickRecord(data.recordingInfo),
			streamInfo: pickRecord(data.streamInfo),
			trackInfo: pickRecord(data.track ?? data.trackInfo),
			raw: cloneRaw(data, id),
		};
	},
};

function ensureString(value: unknown): string | undefined {
	if (typeof value === "string" && value.trim().length > 0) {
		return value;
	}
	return undefined;
}

function parseDate(value: unknown): Date {
	if (value instanceof Date) {
		return value;
	}
	if (value instanceof Timestamp) {
		return value.toDate();
	}
	if (typeof value === "number") {
		const millis = value >= 1_000_000_000_000 ? value : value * 1000;
		return new Date(millis);
	}
	if (typeof value === "string") {
		const numericValue = Number(value);
		if (!Number.isNaN(numericValue)) {
			return parseDate(numericValue);
		}
		const parsed = Date.parse(value);
		if (!Number.isNaN(parsed)) {
			return new Date(parsed);
		}
	}
	if (isRecord(value) && typeof value.seconds === "number") {
		const millis =
			value.seconds * 1000 +
			(typeof value.nanoseconds === "number"
				? value.nanoseconds / 1_000_000
				: 0);
		return new Date(millis);
	}
	return new Date(0);
}

function parseRoom(value: unknown): LiveKitRoom | undefined {
	if (!isRecord(value)) {
		return undefined;
	}

	return {
		sid: ensureString(value.sid) ?? "unknown",
		name: ensureString(value.name) ?? "Unknown room",
		creationTime: parseNumber(value.creationTime ?? value.creation_time),
		emptyTimeout: parseNumber(value.emptyTimeout ?? value.empty_timeout),
		maxParticipants: parseNumber(
			value.maxParticipants ?? value.max_participants,
		),
		metadata: ensureString(value.metadata),
		numParticipants: parseNumber(
			value.numParticipants ?? value.num_participants,
		),
	};
}

function parseParticipant(value: unknown): LiveKitParticipant | undefined {
	if (!isRecord(value)) {
		return undefined;
	}

	const identity = ensureString(value.identity) ?? "";

	return {
		sid: ensureString(value.sid) ?? "unknown",
		identity,
		name: ensureString(value.name) ?? identity,
		state: ensureString(value.state ?? value.status),
		joinedAt: parseTimestampSeconds(value.joinedAt ?? value.joined_at),
		metadata: ensureString(value.metadata),
		permissions: pickRecord(value.permissions),
		disconnectedAt: parseTimestampSeconds(
			value.disconnectedAt ?? value.disconnected_at,
		),
		updatedAt: parseTimestampSeconds(value.updatedAt ?? value.updated_at),
	};
}

function parseNumber(value: unknown): number | undefined {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === "string") {
		const parsed = Number(value);
		if (!Number.isNaN(parsed)) {
			return parsed;
		}
	}
	return undefined;
}

function parseTimestampSeconds(value: unknown): number | undefined {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value >= 1_000_000_000_000
			? Math.floor(value / 1000)
			: Math.floor(value);
	}
	if (typeof value === "string") {
		const numericValue = Number(value);
		if (!Number.isNaN(numericValue)) {
			return parseTimestampSeconds(numericValue);
		}
		const parsed = Date.parse(value);
		if (!Number.isNaN(parsed)) {
			return Math.floor(parsed / 1000);
		}
	}
	if (isRecord(value) && typeof value.seconds === "number") {
		return Math.floor(value.seconds);
	}
	return undefined;
}

function pickRecord(value: unknown): Record<string, unknown> | undefined {
	if (isRecord(value)) {
		return value;
	}
	return undefined;
}

function cloneRaw(
	value: Record<string, unknown>,
	id: string,
): Record<string, unknown> {
	try {
		return JSON.parse(JSON.stringify({ ...value, id })) as Record<
			string,
			unknown
		>;
	} catch {
		return { ...value, id };
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

export function liveKitEventsCollection(
	db: Firestore,
): CollectionReference<LiveKitEventRecord> {
	return collection(db, "liveKitEvents").withConverter(liveKitEventConverter);
}

export function liveKitEventDocument(
	db: Firestore,
	eventId: string,
): DocumentReference<LiveKitEventRecord> {
	return doc(db, "liveKitEvents", eventId).withConverter(liveKitEventConverter);
}

export async function fetchLiveKitEvent(
	db: Firestore,
	eventId: string,
): Promise<LiveKitEventRecord | null> {
	const snapshot = await getDoc(liveKitEventDocument(db, eventId));
	return snapshot.exists() ? snapshot.data() : null;
}

export function compareLiveKitEventRecords(
	a: LiveKitEventRecord,
	b: LiveKitEventRecord,
): number {
	const aTime = a.createdAt.getTime();
	const bTime = b.createdAt.getTime();
	return bTime - aTime;
}

export function toLiveKitUiEvent(record: LiveKitEventRecord) {
	const room = record.room ?? {
		sid: "unknown",
		name: "Unknown room",
		creationTime: 0,
		emptyTimeout: 0,
		maxParticipants: 0,
		numParticipants: 0,
	};

	const participant = record.participant
		? {
				sid: record.participant.sid,
				identity: record.participant.identity ?? "",
				name: record.participant.name ?? record.participant.identity ?? "",
				state: record.participant.state ?? "unknown",
				joinedAt: record.participant.joinedAt ?? 0,
			}
		: null;

	return {
		id: record.id,
		timestamp: formatDisplayTimestamp(record.createdAt),
		name: record.type,
		room: {
			sid: room.sid,
			name: room.name ?? "Unknown room",
			creationTime: room.creationTime ?? 0,
			emptyTimeout: room.emptyTimeout ?? 0,
			maxParticipants: room.maxParticipants ?? 0,
			numParticipants: room.numParticipants ?? 0,
		},
		participant,
		raw: record.raw,
	};
}

function formatDisplayTimestamp(date: Date): string {
	if (Number.isNaN(date.getTime())) {
		return "Unknown";
	}
	return date.toLocaleString();
}

export type LiveKitEvent = ReturnType<typeof toLiveKitUiEvent>;
